import moment, {Moment} from 'moment';

import {
  IFarmInputInvoice,
  IFarmInputProgram,
  IFarmInputQuote,
  IFdApplicationType,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {_NumberWithSpaces, _ToTitleCase} from 'constants/index';

import {AnalyticsBreakdownRow, AnalyticsTopProductRow} from './analyticsTypes';

type SupplierUserProfileWithCompanyType = ISupplierUserProfile & {
  company?: ISupplierUserProfile['company'] & {
    companyType?: {name?: string};
  };
  companyType?: {name?: string};
  isEnterpriseGroup?: boolean;
};

export function lshShouldShowSupplierEngagementAnalytics(
  user: ISupplierUserProfile,
) {
  const supplierUser = user as SupplierUserProfileWithCompanyType;
  const companyType = String(
    supplierUser.company?.companyType?.name ||
      supplierUser.companyType?.name ||
      '',
  ).toLowerCase();

  return (
    !user?.isCommodityGroup &&
    !Boolean(supplierUser.isEnterpriseGroup) &&
    !companyType.includes('enterprise') &&
    Boolean(user?.brand?.id)
  );
}

export function lshFormatCurrency(value: number) {
  return `R ${_NumberWithSpaces(Number(value || 0))}`;
}

export function lshFormatInteger(value: number) {
  return Math.round(Number(value || 0)).toLocaleString('en-ZA');
}

export function lshFormatTitle(value?: string) {
  if (!value) return 'Unknown';
  return _ToTitleCase(String(value).replaceAll('_', ' '));
}

export function lshIsDateWithinRange(
  value: string | undefined,
  startDate: Moment,
  endDate: Moment,
) {
  if (!value) return false;
  const date = moment(value);
  return (
    date.isValid() &&
    date.isSameOrAfter(startDate, 'day') &&
    date.isSameOrBefore(endDate, 'day')
  );
}

export function lshFundingAskValue(application: IFdApplicationType) {
  return (
    application.fundingAsk?.fundingRequestCosts?.reduce(
      (total, cost) => total + Number(cost.cost || 0),
      0,
    ) || 0
  );
}

function lshToBreakdownRows(
  rows: AnalyticsBreakdownRow[],
  totalValue?: number,
  totalCount?: number,
) {
  return rows
    .map((row) => {
      const valueBase = totalValue || 0;
      const countBase = totalCount || 0;
      const percent =
        row.percent ??
        (valueBase && row.value !== undefined
          ? Math.round((row.value / valueBase) * 100)
          : countBase && row.count !== undefined
          ? Math.round((row.count / countBase) * 100)
          : 0);

      return {...row, percent};
    })
    .sort((a, b) => (b.value || b.count || 0) - (a.value || a.count || 0));
}

export function lshBuildFundingStatusRows(applications: IFdApplicationType[]) {
  const totalAsk = applications.reduce(
    (total, application) => total + lshFundingAskValue(application),
    0,
  );
  const map = new Map<string, AnalyticsBreakdownRow>();

  applications.forEach((application) => {
    const label = lshFormatTitle(application.applicationState);
    const current = map.get(label) || {label, count: 0, value: 0};
    current.count = (current.count || 0) + 1;
    current.value = (current.value || 0) + lshFundingAskValue(application);
    map.set(label, current);
  });

  return lshToBreakdownRows(Array.from(map.values()), totalAsk);
}

export function lshBuildFundingCategoryRows(
  applications: IFdApplicationType[],
) {
  const map = new Map<string, AnalyticsBreakdownRow>();

  applications.forEach((application) => {
    application.fundingAsk?.fundingRequestCosts?.forEach((cost) => {
      const label = cost.label || 'Unknown cost';
      const current = map.get(label) || {label, count: 0, value: 0};
      current.count = (current.count || 0) + 1;
      current.value = (current.value || 0) + Number(cost.cost || 0);
      map.set(label, current);
    });
  });

  const totalAsk = Array.from(map.values()).reduce(
    (total, row) => total + Number(row.value || 0),
    0,
  );

  return lshToBreakdownRows(Array.from(map.values()), totalAsk).slice(0, 8);
}

export function lshBuildProgramRows(
  programs: IFarmInputProgram[],
  keyGetter: (program: IFarmInputProgram) => string | undefined,
) {
  const map = new Map<string, AnalyticsBreakdownRow>();

  programs.forEach((program) => {
    const label = lshFormatTitle(keyGetter(program));
    const current = map.get(label) || {label, count: 0, value: 0};
    current.count = (current.count || 0) + 1;
    current.value = (current.value || 0) + Number(program.hectares || 0);
    map.set(label, current);
  });

  const totalHectares = Array.from(map.values()).reduce(
    (total, row) => total + Number(row.value || 0),
    0,
  );

  return lshToBreakdownRows(Array.from(map.values()), totalHectares);
}

export function lshBuildTopQuoteProducts(quotes: IFarmInputQuote[]) {
  const map = new Map<string, AnalyticsTopProductRow>();

  quotes.forEach((quote) => {
    quote.lineItems?.forEach((line) => {
      const name = line.product?.name || line.product?.stockCode || 'Product';
      const current = map.get(name) || {name, quantity: 0, value: 0};
      const quantity = Number(line.quantity || line.originalQuantity || 0);
      const price = Number(line.price || line.retailPrice || 0);
      current.quantity += quantity;
      current.value += quantity * price;
      map.set(name, current);
    });
  });

  return Array.from(map.values())
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);
}

export function lshBuildTopOrderProducts(orders: IFarmInputInvoice[]) {
  const map = new Map<string, AnalyticsTopProductRow>();

  orders.forEach((invoice) => {
    invoice.orders?.forEach((order) => {
      const name = order.product?.name || order.product?.stockCode || 'Product';
      const current = map.get(name) || {name, quantity: 0, value: 0};
      const quantity = Number(order.quantity || 0);
      const price = Number(order.price || order.product?.price || 0);
      current.quantity += quantity;
      current.value += quantity * price;
      map.set(name, current);
    });
  });

  return Array.from(map.values())
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);
}
